import type { TFunction } from "i18next";
import type { PermissionRole, UserEntity } from "src/schemas/entitySchemas";
import { identityTranslator } from "./commonUtils";

export interface AccessRule {
  anyPermissions?: PermissionRole[];
  allPermissions?: PermissionRole[];
}

export interface RouteAccessHandle {
  access?: AccessRule;
}

export const ACCESS_RULES: Record<string, AccessRule> = {
  admin: {
    anyPermissions: ["SUPERUSER", "ADMIN"],
  },
  locationsWrite: {
    anyPermissions: ["SUPERUSER", "ADMIN", "W_LOCATION"],
  },
  readersRead: {
    anyPermissions: ["SUPERUSER", "ADMIN", "R_INPUT"],
  },
  readersWrite: {
    anyPermissions: ["SUPERUSER", "ADMIN", "W_INPUT"],
  },
  dataDefinitionsRead: {
    anyPermissions: ["SUPERUSER", "ADMIN", "R_DATADEFINITION"],
  },
  dataDefinitionsWrite: {
    anyPermissions: ["SUPERUSER", "ADMIN", "W_DATADEFINITION"],
  },
  locationSequencesRead: {
    anyPermissions: ["SUPERUSER", "ADMIN", "R_SENSOR", "W_LOCATION"],
  },
  locationSequencesWrite: {
    anyPermissions: ["SUPERUSER", "ADMIN", "W_LOCATION"],
  },
  traitsRead: {
    anyPermissions: ["SUPERUSER", "ADMIN", "R_TRAIT"],
  },
  traitValuesWrite: {
    anyPermissions: ["SUPERUSER", "ADMIN", "W_TRAIT"],
  },
  traitDefinitionsWrite: {
    anyPermissions: ["SUPERUSER", "ADMIN", "W_TRAIT_DEFINITION"],
  },
  sensorsRead: {
    anyPermissions: ["SUPERUSER", "R_SENSOR"],
  },
  sensorsWrite: {
    anyPermissions: ["SUPERUSER", "W_SENSOR"],
  },
  resourcesRead: {
    anyPermissions: ["SUPERUSER", "R_SENSOR"],
  },
  resourcesWrite: {
    anyPermissions: ["SUPERUSER", "W_SENSOR"],
  },
  inventoryRead: {
    anyPermissions: ["SUPERUSER", "R_INVENTORY"],
  },
  inventoryWrite: {
    anyPermissions: ["SUPERUSER", "W_INVENTORY"],
  },
  floorplansRead: {
    anyPermissions: ["SUPERUSER", "R_FLOORPLAN"],
  },
  floorplansWrite: {
    anyPermissions: ["SUPERUSER", "W_FLOORPLAN"],
  },
  alertsWrite: {
    anyPermissions: ["SUPERUSER", "W_ALERT"],
  },
  licencesRead: {
    anyPermissions: ["SUPERUSER", "ADMIN"],
  },
  licencesWrite: {
    anyPermissions: ["SUPERUSER", "ADMIN"],
  },
} as const;

const hasAnyValue = <T extends string>(
  userValues: readonly T[],
  requiredValues: readonly T[],
) => requiredValues.some((value) => userValues.includes(value));

const hasAllValues = <T extends string>(
  userValues: readonly T[],
  requiredValues: readonly T[],
) => requiredValues.every((value) => userValues.includes(value));

export const hasPermission = (
  user: UserEntity | undefined,
  permission: PermissionRole,
) => !!user?.permissions?.includes(permission);

export const hasAnyPermission = (
  user: UserEntity | undefined,
  permissions: readonly PermissionRole[],
) => hasAnyValue(user?.permissions ?? [], permissions);

export const hasAllPermissions = (
  user: UserEntity | undefined,
  permissions: readonly PermissionRole[],
) => hasAllValues(user?.permissions ?? [], permissions);

export const canAccess = (
  user: UserEntity | undefined,
  rule?: AccessRule,
): boolean => {
  if (!rule) {
    return true;
  }

  const passesAnyPermissions =
    !rule.anyPermissions || hasAnyPermission(user, rule.anyPermissions);
  const passesAllPermissions =
    !rule.allPermissions || hasAllPermissions(user, rule.allPermissions);

  return passesAnyPermissions && passesAllPermissions;
};

const isRouteAccessHandle = (handle: unknown): handle is RouteAccessHandle =>
  !!handle && typeof handle === "object" && "access" in handle;

export const getRouteAccess = (handle: unknown): AccessRule | undefined =>
  isRouteAccessHandle(handle) ? handle.access : undefined;

// Wrapping translation keys in t-function is necessary for extraction by i18next-parser
export const getUserPermissions = (
  t: TFunction,
): { key: PermissionRole; label: string }[] => [
  {
    key: "SUPERUSER",
    label: t("user::permissions::Superuser (Full Platform Access)"),
  },
  {
    key: "ADMIN",
    label: t("user::permissions::Admin (Platform Administration Access)"),
  },
  {
    key: "W_LOCATION",
    label: t("user::permissions::Write Locations"),
  },
  {
    key: "W_INPUT",
    label: t("user::permissions::Write Input Systems"),
  },
  {
    key: "R_INPUT",
    label: t("user::permissions::Read Input Systems"),
  },
  {
    key: "W_DATADEFINITION",
    label: t("user::permissions::Write Data Definitions"),
  },
  {
    key: "R_DATADEFINITION",
    label: t("user::permissions::Read Data Definitions"),
  },
  {
    key: "W_TRAIT_DEFINITION",
    label: t("user::permissions::Write Trait Definitions and Sensor Traits"),
  },
  {
    key: "R_TRAIT",
    label: t("user::permissions::Read Trait Definitions and Sensor Traits"),
  },
  {
    key: "W_TRAIT",
    label: t("user::permissions::Write Trait Values"),
  },
  {
    key: "W_SENSOR",
    label: t("user::permissions::Write Sensors"),
  },
  {
    key: "R_SENSOR",
    label: t("user::permissions::Read Sensors"),
  },
  {
    key: "W_INVENTORY",
    label: t("user::permissions::Write Inventory Data"),
  },
  {
    key: "R_INVENTORY",
    label: t("user::permissions::Read Inventory Data"),
  },
  {
    key: "W_FLOORPLAN",
    label: t("user::permissions::Write Floor Plans"),
  },
  {
    key: "R_FLOORPLAN",
    label: t("user::permissions::Read Floor Plans"),
  },
  {
    key: "W_ALERT",
    label: t("user::permissions::Write Alerts"),
  },
];

// Create a separate translation lookup constant with identity translator
const userPermissionsUntranslated = getUserPermissions(identityTranslator);

export const getUserPermissionTranslation = (
  key: PermissionRole,
  t: TFunction,
): string => {
  const permission = userPermissionsUntranslated.find((p) => p.key === key);
  return permission ? t(permission.label) : key;
};
